import {
  AlertCircleIcon,
  CheckCircleIcon,
  InfoCircleIcon,
} from "@talladega/icons";
import classNames from "classnames";
import React from "react";

export type AlertProps = {
  mode?: "info" | "success" | "warning" | "error" | "default";
  heading: string;
  subheading?: string;
  className?: string;
  children?: React.ReactNode;
  size?: "sm" | "md" | "lg";
};

const alertConfig = {
  info: {
    Icon: InfoCircleIcon,
    containerClasses: "bg-info-primary border-info-subtle",
    iconClasses: "text-utility-blue-light-600",
  },
  success: {
    Icon: CheckCircleIcon,
    containerClasses: "bg-success-primary border-success-subtle",
    iconClasses: "text-success-primary",
  },
  warning: {
    Icon: InfoCircleIcon,
    containerClasses: "bg-warning-primary border-warning-subtle",
    iconClasses: "text-warning-500",
  },
  error: {
    Icon: AlertCircleIcon,
    containerClasses: "bg-error-primary border-error-subtle",
    iconClasses: "text-error-600",
  },
  default: {
    Icon: InfoCircleIcon,
    containerClasses: "bg-secondary border-secondary",
    iconClasses: "text-secondary-500",
  },
};

const sizeConfig = {
  sm: {
    text: "text-sm",
    icon: "h-5 w-5",
  },
  md: {
    text: "text-base",
    icon: "h-6 w-6",
  },
  lg: {
    text: "text-lg",
    icon: "h-7 w-7",
  },
};

export const Alert = ({
  mode = "default",
  heading,
  subheading,
  className,
  children,
  size = "sm",
}: AlertProps) => {
  const { Icon, containerClasses, iconClasses } = alertConfig[mode];
  const { text: textSize, icon: iconSize } = sizeConfig[size];

  return (
    <div
      role="alert"
      className={classNames(
        "border-2 p-4 rounded-xl",
        containerClasses,
        className
      )}
    >
      <div className="flex gap-4">
        <div className="flex-shrink-0">
          <Icon
            className={classNames(iconSize, iconClasses)}
            aria-hidden="true"
          />
        </div>
        <div className="flex flex-col items-start gap-2">
          {heading && (
            <h3 className={classNames(textSize, "font-bold text-current")}>
              {heading}
            </h3>
          )}
          {subheading && (
            <div className={classNames(textSize, "text-current")}>
              <p>{subheading}</p>
            </div>
          )}
          {children && (
            <div className={classNames(textSize, "text-current mt-2")}>
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
